import * as React from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  ValueAssociation,
  ReferenceNode,
  ParameterType,
  Reference,
  FrameItem
} from '@interface/index';
import { NiTable, BodyGroup, AddButton } from '@component/index';
import { NiCommonCascader, SELECTION_KEY, SelectionOption, renderSelectionVariable } from '@config/index';
import { isEqual, ParameterMapStore } from '@lib/index';
import { localizedString } from '@locale/index';

import '@style/component/value-association/index.less';

interface ValueAssociationTableProps {
  disabled?: boolean;
  frame: FrameItem;
  data: ValueAssociation[];
  onChange: (next: ValueAssociation[]) => void;
}

interface ValueAssociationTableState {
  data: ValueAssociation[];
  curRecord?: ValueAssociation;
}

export class ValueAssociationTable extends React.Component<ValueAssociationTableProps, ValueAssociationTableState> {
  constructor(props: ValueAssociationTableProps) {
    super(props);

    this.state = {
      data: props.data
    };
  }

  componentDidUpdate(prevProps: ValueAssociationTableProps, prevState: ValueAssociationTableState) {
    if (!isEqual(prevProps, this.props)) {
      this.setState({ data: this.props.data });
    }
  }

  render() {
    const {
      data
    } = this.state;
    const { disabled } = this.props;

    return <BodyGroup
      title={localizedString('association')}
      extra={<AddButton
        disabled={disabled}
        onClick={this.handleNewAssociation}
      />}
    >
      <NiTable className="value-association"
        rowKey={((record: ValueAssociation) =>
          `value-association-row-${record.variable.toString()}`)}
        dataSource={data}
        pagination={false}
        columns={this.getColumns()}
      />
    </BodyGroup>;
  }

  getColumns = () => {
    const { onChange, frame, disabled } = this.props;
    const { data } = this.state;
    return [{
      width: '45%',
      title: localizedString('association_target'),
      dataIndex: 'variable',
      render: (item: string[], record: ValueAssociation) => {
        const disabledValues = data.map((va: ValueAssociation) =>
          va.variable.map((variable: ReferenceNode) => variable.id));

        return <NiCommonCascader
          className="slot-variable-select"
          disabled={disabled}
          selectionkey={SELECTION_KEY.AVAILABLE_ASSOCIATION}
          rootpath={[{ value: frame.id || '', value_type: frame.type }]}
          value={record.variable.length
            ? record.variable.map((item: ReferenceNode) => item.id)
            : undefined}
          allowClear={false}
          disabledvalues={disabledValues}
          displayRender={(label: string[]) =>
            renderSelectionVariable(record.variable)}
          onChange={(value: string[], selectedOptions?: SelectionOption[]) => {
            if (!selectedOptions) {
              return;
            }

            const selected = selectedOptions &&
              selectedOptions.map((opt: SelectionOption) => ({
                id: opt.value || '',
                name: opt.label || '',
                type: opt.valueType as ParameterType
              })) || [];

            if (isEqual(record.variable, selected)) {
              return;
            }

            record.variable = selected as Reference;
            record.defaultValues = [[]];

            const lastOpt = selectedOptions[selectedOptions.length - 1];

            if (lastOpt.valueType === ParameterType.ATTRIBUTE) {
              record.variableType = lastOpt.payload && lastOpt.payload.type.id || '';
            } else if (lastOpt.valueType === ParameterType.CLOSURE) {
              record.variableType = lastOpt.payload && lastOpt.payload.type && lastOpt.payload.type.id;
            }

            this.setState({ data });
          }}
        />;
      }
    }, {
      width: '45%',
      title: localizedString('association_default_value'),
      dataIndex: 'defaultValues',
      render: (item: Reference[], record: ValueAssociation, index: number) => {

        return <div className="default-value-wrap">
          <ul>
            {item.map((defaultValue: Reference, defaultValueIndex: number) =>
              <li key={`default-value-${defaultValueIndex}`}>
                <div className="default-value-row">
                  <NiCommonCascader
                    disabled={disabled}
                    className="default-value-select"
                    key={record.variable.map((node: ReferenceNode) => node.id).join('.')}
                    selectionkey={SELECTION_KEY.AVAILABLE_ASSOCIATION}
                    allowClear={false}
                    optionFilter={(opt: SelectionOption) => {
                      if (!opt.isLeaf) {
                        return true;
                      }

                      let optVariableType;

                      if (opt.valueType === ParameterType.ATTRIBUTE) {
                        optVariableType = opt.payload && opt.payload.type.id || '';
                      } else if (opt.valueType === ParameterType.CLOSURE) {
                        optVariableType = opt.payload && opt.payload.type && opt.payload.type.id;
                      }

                      const lastNode = record.variable[record.variable.length - 1];
                      const variableMulti = ParameterMapStore.get(lastNode.id).multiValue;
                      const valueMulti = opt.payload.multi_value;

                      return lastNode &&
                        optVariableType &&
                        optVariableType === record.variableType &&
                        opt.value !== lastNode.id &&
                        variableMulti === valueMulti;
                    }}
                    rootpath={[{ value: frame.id || '', value_type: frame.type }]}
                    value={defaultValue.map((value: ReferenceNode) => value.id)}
                    displayRender={(label: string[]) =>
                      renderSelectionVariable(defaultValue)}
                    onChange={(
                      value: string[],
                      selectedOptions?: SelectionOption[]
                    ) => {
                      record.defaultValues[defaultValueIndex] = selectedOptions &&
                        selectedOptions.map((opt: SelectionOption) => ({
                          id: opt.value as string,
                          name: opt.label as string,
                          type: opt.valueType as ParameterType
                        })) || [];

                      if (record.inheritedFromCompositeId) {
                        delete record.inheritedFromCompositeId;
                      }

                      this.setState({ data });

                      onChange(data);
                    }}
                  />
                  <div
                    className="icon-area"
                  >
                    {defaultValueIndex !== 0
                      && !disabled
                      && <DeleteOutlined className="delete-icon" onClick={() => {
                        this.handleDeleteDefaultValue(index, defaultValueIndex);
                      }} />}
                  </div>
                </div>
              </li>
            )}
          </ul>
          <div
            className="icon-area"
          >{!disabled &&
            <PlusOutlined className="add-icon" onClick={() => {
              this.handleNewDefaultValue(index);
            }} />}

          </div>
        </div>;
      }
    },
    {
      width: '10%',
      title: 'Source',
      dataIndex: 'inheritedFromCompositeName',
      key: 'source',
      render: (sourceName: string, record: ValueAssociation) => {
        return sourceName || (record.isOverwrite ? 'Local+' : 'Local');
      }
    },
    {
      title: '',
      width: 50,
      dataIndex: 'operation',
      className: 'hide',
      render: (operation: any, record: ValueAssociation, index: number) => (
        <div
          className="icon-area"
        >
          {!disabled &&
            <DeleteOutlined className="delete-btn" onClick={() => {
              this.handleDeleteAssociation(index);
            }} />
          }
        </div>
      )
    }];
  }

  handleNewAssociation = () => {
    const { data } = this.state;

    const unfinishItem = data.find((item: ValueAssociation) => {
      let finished = true;

      if (!item.variable || !item.variable.length) {
        finished = false;
      } else if (!item.defaultValues || !item.defaultValues.length) {
        finished = false;
      } else {
        for (const value of item.defaultValues) {
          if (!value.length) {
            finished = false;
          }
        }
      }

      return !finished;
    });

    if (unfinishItem) {
      return;
    }

    this.setState({
      data: [
        ...data,
        {
          variable: [],
          variableType: '',
          defaultValues: [[]]
        }
      ]
    });
  }

  handleDeleteAssociation = (index: number) => {
    const { data } = this.state;
    data.splice(index, 1);
    this.setState({ data });
    this.props.onChange(data);
  }

  handleNewDefaultValue = (index: number) => {
    const { data } = this.state;

    const defaultValues = data[index].defaultValues;

    for (const value of defaultValues) {
      if (!value.length) {
        return;
      }
    }

    data[index].defaultValues.push([]);
    this.setState({ data });
  }

  handleDeleteDefaultValue = (index: number, defaultValueIndex: number) => {
    const { data } = this.state;
    data[index].defaultValues.splice(defaultValueIndex, 1);
    this.setState({ data });
    this.props.onChange(data);
  }
}
